import { useEffect, useRef } from 'react';
import { MapSourceDataEvent } from 'mapbox-gl';

import { useMap } from './useMap';

export const useSourceLoadingHandlers = ({
  id,
  onLoadStart,
  onLoadFinish
}: {
  id: string;
  onLoadStart?: () => void;
  onLoadFinish?: () => void;
}) => {
  const map = useMap();
  const loaderTimerId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSourceDataLoaded = useRef(false);

  useEffect(() => {
    const handleIdle = () => {
      map?.off('sourcedataloading', handleSourceDataLoading);
      map?.off('idle', handleIdle);
      if (loaderTimerId.current !== null) {
        clearTimeout(loaderTimerId.current);
        loaderTimerId.current = null;
      }
      isSourceDataLoaded.current = true;
      onLoadFinish?.();
    };

    const handleSourceDataLoading = (e: MapSourceDataEvent) => {
      if (
        !isSourceDataLoaded.current &&
        !e.isSourceLoaded &&
        e.sourceId === id
      ) {
        if (loaderTimerId.current !== null) {
          clearTimeout(loaderTimerId.current);
        }
        loaderTimerId.current = setTimeout(() => {
          onLoadStart?.();
        }, 600);

        map?.on('idle', handleIdle);
      }
    };

    map?.on('sourcedataloading', handleSourceDataLoading);

    return () => {
      if (loaderTimerId.current !== null) {
        clearTimeout(loaderTimerId.current);
        loaderTimerId.current = null;
      }
      isSourceDataLoaded.current = false;

      map?.off('sourcedataloading', handleSourceDataLoading);
    };
    // We only want to define the event listener once, so the empty dependency array is
    // intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
